import useSWR from 'swr';
import { backendClient } from '@/lib/api-client';

const fetcher = async (url: string) => {
  const response = await backendClient.get(url);
  return response.data;
};

/**
 * Hook to check if the current user is a tenant admin (has Admin role).
 * Uses SWR with localStorage provider to prevent layout shifts.
 *
 * This is distinct from useSuperAdminCheck which checks for Crimson team members.
 * This hook checks for organization-level admins who can view analytics.
 */
export const useAdminCheck = () => {
  // Only use fallback if we have a cached value
  const cachedValue = typeof window !== 'undefined' ? localStorage.getItem('is_tenant_admin') : null;

  const { data, isLoading, error } = useSWR('/analytics/check-access', fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    dedupingInterval: 300000, // 5 minutes
    // Only provide fallback data if we have a cached value
    ...(cachedValue !== null && {
      fallbackData: { is_admin: cachedValue === 'true' }
    }),
    onSuccess: (data) => {
      if (typeof window !== 'undefined') {
        localStorage.setItem('is_tenant_admin', String(data?.is_admin || false));
      }
    },
  });

  // Use cached value immediately if data hasn't loaded yet to prevent flicker
  const isAdmin = data?.is_admin ?? (cachedValue === 'true');

  return {
    isAdmin,
    isLoading,
    error,
  };
};
